import {AxiosError} from "axios";
import {ApiConfig} from "components/src/api/admin/ApiConfig";
import {
    CertificateApi,
    CertificateStore,
    CertificateValidationApi,
    type CertificateValidationResult,
    CTLApi,
    CTLEntryStatus,
    Expired,
    Match,
    MissingKeyAlias,
    type X509CertificateDetails
} from "components/src/api/admin/generated";
import {CertificateExportDialog} from "components/src/certificate/CertificateExportDialog";
import {CertificateFilter} from "components/src/certificate/CertificateFilter";
import {CertificateImportDialog} from "components/src/certificate/CertificateImportDialog";
import {CertificateRow} from "components/src/certificate/CertificateRow";
import {CertificateTableHeader} from "components/src/certificate/CertificateTableHeader";
import {PrivateKeyImportDialog} from "components/src/certificate/PrivateKeyImportDialog";
import {SearchField} from "components/src/certificate/SearchField";
import {ActionsDropdown} from "components/src/common/ActionsDropdown";
import {ClearSelection} from "components/src/common/ClearSelection";
import {ConfirmDialog} from "components/src/common/ConfirmDialog";
import {DialogCloseCause} from "components/src/common/Enums";
import {InvertSelection} from "components/src/common/InvertSelection";
import {Pager} from "components/src/common/Pager";
import {RowsPerPage} from "components/src/common/RowsPerPage";
import {useSpinnerStatusContext} from "components/src/hook/UseSpinnerStatusContext";
import {useTranslationWithOverride} from "components/src/hook/UseTranslationWithOverride";
import {PageAlert} from "components/src/layout/PageAlert.tsx";
import {PageContent} from "components/src/layout/PageContent.tsx";
import {PageFooter} from "components/src/layout/PageFooter.tsx";
import {PageHeader} from "components/src/layout/PageHeader.tsx";
import {logger} from "components/src/utils/Logger";
import {Utils} from "components/src/utils/Utils";
import {
    Bin as DeleteIcon,
    CardShield as SMIMEIcon,
    CardShield as CertificateImportIcon,
    Check as TrustIcon,
    DatabaseExport as ExportIcon,
    KeyPlus as KeyImportIcon,
    Refresh as ResetTrustIcon,
    Xmark as DistrustIcon
} from "iconoir-react";
import {Dispatch, SetStateAction, useCallback, useEffect, useState} from 'react'
import {Dropdown, Navbar} from "react-bootstrap";
import Col from 'react-bootstrap/Col';
import Form from 'react-bootstrap/Form';
import Nav from "react-bootstrap/Nav";
import Row from 'react-bootstrap/Row';
import Table from 'react-bootstrap/Table';
import {useLocation, useNavigate} from "react-router-dom";

/*
 * REST api for accessing the back-end
 */
const certificateApi = new CertificateApi(ApiConfig.config());
const certificateValidationApi = new CertificateValidationApi(ApiConfig.config());
const ctlApi = new CTLApi(ApiConfig.config());

/**
 * Certificates class represents a component that manages certificates.
 * It provides methods to load, display import and export certificates, as well as delete selected certificates.
 * Certificates from the Certificates store (end-user and intermediate) and Root store can be
 * managed.
 */
export const Certificates = () => {
    /**
     * Navigate is used to navigate to the login page if the user is no longer authenticated
     */
    const navigate = useNavigate();
    /**
     * Represents the current location object in a React Router application.
     */
    const location = useLocation()
    /**
     * The loaded certificates
     */
    const [certificates, setCertificates] = useState<X509CertificateDetails[]>([]);
    /**
     * The total number of certificates
     */
    const [certificateCount, setCertificateCount] = useState<number>(0);
    /**
     * The error message if some general error occurred
     */
    const [errorMessage, setErrorMessage] = useState<string>();
    /**
     * The index of the current page
     */
    const [currentPage, setCurrentPage] = useState<number>(1);
    /**
     * The max number of certificates to show
     */
    const [rowsPerPage, setRowsPerPage] = useState<number>(40);
    /**
     * The selected certificates
     */
    const [selectedCertificates, setSelectedCertificates] = useState<X509CertificateDetails[]>([]);
    /**
     * If true, the "confirm deletion" modal dialog will be shown
     */
    const [showDeleteConfirm, setShowDeleteConfirm] = useState<boolean>(false);
    /**
     * If true, the export certificates dialog will be shown
     */
    const [showCertificateExportDialog, setShowCertificateExportDialog] = useState<boolean>(false);
    /**
     * If true, the import certificates dialog will be shown
     */
    const [showCertificateImportDialog, setShowCertificateImportDialog] = useState<boolean>(false);
    /**
     * If true, the import private keys dialog will be shown
     */
    const [showPrivateKeyImportDialog, setShowPrivateKeyImportDialog] = useState<boolean>(false);
    /**
     * Incremented whenever the certificates should be reloaded from the back-end. A counter is used instead of a
     * boolean because every increment is a new value, so a reload is never silently dropped because a previous
     * reload request was still pending
     */
    const [reloadCounter, setReloadCounter] = useState<number>(0);
    /**
     * The active certificate store (certificates or root)
     */
    const [certificateStore, setCertificateStore] = useState<CertificateStore>(location?.state?.certificateStore??CertificateStore.Certificates);
    /**
     * Determines whether expired certificates are shown
     */
    const [expired, setExpired] = useState<Expired>(location?.state?.expired??Expired.All);
    /**
     * Determines whether certificates without private keys are shown
     */
    const [missingKeyAlias, setMissingKeyAlias] = useState<MissingKeyAlias>(location?.state?.missingKeyAlias??MissingKeyAlias.Allowed);
    /**
     * The field to search on
     */
    const [searchField, setSearchField] = useState<SearchField>(SearchField.Email);
    /**
     * The field which is searched on
     */
    const [activeSearchValue, setActiveSearchValue] = useState<string>();
    /**
     * The field of the search box. This value will be copied to the activeSearchValue when apply is clicked
     */
    const [searchValue, setSearchValue] = useState<string>();
    /**
     * Keeps track of activities for which the spinner should be shown. If an activity is started, the counter should
     * be increased by one and after the activity
     */
    const {setSpinnerStateCounter} = useSpinnerStatusContext();
    /**
     * i18next translation hook
     */
    const {t} = useTranslationWithOverride();
    /**
     * Forces the certificates and the certificate count to be reloaded from the back-end
     */
    const forceReload = useCallback(() => setReloadCounter(counter => counter + 1), []);

    useEffect(() => {
        logger.debug(`Getting certificates count from ${certificateStore}`)

        if (setSpinnerStateCounter !== undefined) {
            setSpinnerStateCounter(count => count + 1);
        }

        // Set when this effect is cleaned up, i.e., when a newer request has superseded this one. The response of a
        // superseded request must be ignored because it can arrive after the response of the newer request
        let superseded = false;

        let loadCertificateCountCallback;

        if ((activeSearchValue??"").length > 0)
        {
            switch (searchField)
            {
                case SearchField.Email:
                    loadCertificateCountCallback = async () => {
                        return await certificateApi.certificateControllerSearchForCertificatesByEmailCount(
                            certificateStore, `%${activeSearchValue??""}%`, Match.Like, expired, missingKeyAlias);
                    };
                    break;
                case SearchField.Subject:
                    loadCertificateCountCallback = async () => {
                        return await certificateApi.certificateControllerSearchForCertificatesBySubjectCount(
                            certificateStore, `%${activeSearchValue??""}%`, expired, missingKeyAlias);
                    };
                    break;
                case SearchField.Issuer:
                    loadCertificateCountCallback = async () => {
                        return await certificateApi.certificateControllerSearchForCertificatesByIssuerCount(
                            certificateStore, `%${activeSearchValue??""}%`, expired, missingKeyAlias);
                    };
                    break;
            }
        }
        else {
            loadCertificateCountCallback = async () => {
                return await certificateApi.certificateControllerGetCertificatesCount(certificateStore, expired, missingKeyAlias);
            };
        }

        loadCertificateCountCallback()
            .then(response => {
                if (superseded) {
                    return;
                }

                setCertificateCount(response.data);
            })
            .catch((err: Error | AxiosError) => {
                logger.error("Error getting certificates count", err);
                Utils.redirectToLoginIfNotAuthenticated(err, navigate);

                if (superseded) {
                    return;
                }

                setErrorMessage(Utils.getErrorMessage(err, t));
            })
            .finally(() => {
                if (setSpinnerStateCounter !== undefined) {
                    setSpinnerStateCounter(count => count - 1);
                }
            })

        return () => {
            superseded = true;
        }
    }, [certificateStore,
        expired,
        missingKeyAlias,
        activeSearchValue,
        searchField,
        navigate,
        setSpinnerStateCounter,
        reloadCounter,
        t
    ]);

    useEffect(() => {
        logger.debug(`Getting certificates from ${certificateStore}`)

        if (setSpinnerStateCounter !== undefined) {
            setSpinnerStateCounter(count => count + 1);
        }

        // reset state when we load new data
        setSelectedCertificates([]);
        // Note: do not reset errorMessage because otherwise the message might get lost when re-rendering
        //       The error message can be closed by clicking the close icon

        // Set when this effect is cleaned up, i.e., when a newer request has superseded this one. The response of a
        // superseded request must be ignored because it can arrive after the response of the newer request. Without
        // this check, rapidly paging through the certificates can leave the table showing the certificates of a page
        // other than the page the pager reports
        let superseded = false;

        let loadCertificatesCallback;

        if ((activeSearchValue??"").length > 0)
        {
            switch (searchField)
            {
                case SearchField.Email:
                    loadCertificatesCallback = async () => {
                        return await certificateApi.certificateControllerSearchForCertificatesByEmail(
                            certificateStore, `%${activeSearchValue??""}%`, Match.Like, expired, missingKeyAlias,
                            (currentPage - 1) * rowsPerPage, rowsPerPage);
                    };
                    break;
                case SearchField.Subject:
                    loadCertificatesCallback = async () => {
                        return await certificateApi.certificateControllerSearchForCertificatesBySubject(
                            certificateStore, `%${activeSearchValue??""}%`, expired, missingKeyAlias,
                            (currentPage - 1) * rowsPerPage, rowsPerPage);
                    };
                    break;
                case SearchField.Issuer:
                    loadCertificatesCallback = async () => {
                        return await certificateApi.certificateControllerSearchForCertificatesByIssuer(
                            certificateStore, `%${activeSearchValue??""}%`, expired, missingKeyAlias,
                            (currentPage - 1) * rowsPerPage, rowsPerPage);
                    };
                    break;
            }
        }
        else {
            loadCertificatesCallback = async () => {
                return await certificateApi.certificateControllerGetCertificates(certificateStore, expired,
                    missingKeyAlias, (currentPage - 1) * rowsPerPage, rowsPerPage);
            };
        }

        loadCertificatesCallback().then(response => {
                if (superseded) {
                    return;
                }

                setCertificates(response.data);
            })
            .catch((err: Error | AxiosError) => {
                logger.error("Error getting certificates", err);
                Utils.redirectToLoginIfNotAuthenticated(err, navigate);

                if (superseded) {
                    return;
                }

                setErrorMessage(Utils.getErrorMessage(err, t));
            })
            .finally(() => {
                if (setSpinnerStateCounter !== undefined) {
                    setSpinnerStateCounter(count => count - 1);
                }
            })

        return () => {
            superseded = true;
        }
    }, [certificateStore,
        expired,
        missingKeyAlias,
        activeSearchValue,
        searchField,
        navigate,
        setSpinnerStateCounter,
        reloadCounter,
        currentPage,
        rowsPerPage,
        t
    ]);

    const deleteSelected = () =>
    {
        if (setSpinnerStateCounter !== undefined) {
            setSpinnerStateCounter(count => count + 1);
        }

        certificateApi.certificateControllerDeleteCertificates(certificateStore, selectedCertificates.map(cert => cert.thumbprint))
            .then((response) => {
                // check if there are certificate which could not be deleted because there are in use
                const notDeletedThumbprints : string [] = response.data;

                if (notDeletedThumbprints.length > 0)
                {
                    setErrorMessage(notDeletedThumbprints.length > 1 ?
                        t("certificates.some-certificates-not-deleted", {thumbprints: notDeletedThumbprints.join(", ")})
                        :
                        t("certificates.certificate-not-deleted", {thumbprints: notDeletedThumbprints.join(", ")}))
                }

                forceReload();
            })
            .catch((err: Error | AxiosError) => {
                logger.error("Error deleting certificates", err);
                Utils.redirectToLoginIfNotAuthenticated(err, navigate);
                setErrorMessage(Utils.getErrorMessage(err, t));
            }).finally(() => {
                if (setSpinnerStateCounter !== undefined) {
                    setSpinnerStateCounter(count => count - 1);
                }
                setShowDeleteConfirm(false);
                setSelectedCertificates([]);
            })
    }

    /**
     * Called when a certificate is selected (i.e., when the checkbox is checked)
     */
    const onSelectCertificate = (checked : boolean, certificate : X509CertificateDetails) =>
    {
        setSelectedCertificates(checked ?
            [...selectedCertificates, certificate] :
            selectedCertificates.filter(e => e.thumbprint !== certificate.thumbprint)
        );
    }

    /**
     * Inverts the selection of certificates by updating the list of selected certificates.
     * Each previously unselected certificate will be selected, and each previously
     * selected certificate will be unselected.
     */
    const invertSelection = () => {
        setSelectedCertificates([...certificates].filter(cert => !selectedCertificates.includes(cert)));
    }

    /**
     * Checks the validity of a certificate using the certificate validation API.
     *
     * NOTE: this must be wrapped in a useCallback call because the method is provided to CertificateRow
     */
    const checkCertificateValidity = (useCallback((certificate : X509CertificateDetails, setCertificateValidationResult : Dispatch<SetStateAction<CertificateValidationResult | undefined>>) =>
    {
        if (setSpinnerStateCounter !== undefined) {
            setSpinnerStateCounter(count => count + 1);
        }

        certificateValidationApi.certificateValidationControllerValidateCertificate(certificateStore, certificate.thumbprint)
            .then((response) =>
            {
                const validationResult : CertificateValidationResult = response.data;

                setCertificateValidationResult(validationResult);
            })
            .catch((err: Error | AxiosError) => {
                logger.error("Error certificate validation result", err);
                setCertificateValidationResult(undefined);
            }).finally(() => {
                if (setSpinnerStateCounter !== undefined) {
                    setSpinnerStateCounter(count => count - 1);
                }
            })
    }, [certificateStore,
        setSpinnerStateCounter]));

    /**
     * Is called when the ExportCertificatesDialog is closed
     */
    const onExportCertificatesDialogClose = (closeCause : DialogCloseCause) =>
    {
        setShowCertificateExportDialog(false);

        if (closeCause === DialogCloseCause.Succes) {
            // clear the selected certificates after export
            setSelectedCertificates([]);
        }
    }

    /**
     * Is called when the CertificateImportDialog is closed
     */
    const onCertificateImportDialogClose = (closeCause : DialogCloseCause) =>
    {
        setShowCertificateImportDialog(false);

        if (closeCause === DialogCloseCause.Succes) {
            forceReload();
        }
    }

    /**
     * Called when the PrivateKeyImportDialog is closed
     */
    const onPrivateKeyImportDialogClose = (closeCause : DialogCloseCause) =>
    {
        setShowPrivateKeyImportDialog(false);

        if (closeCause === DialogCloseCause.Succes) {
            forceReload();
        }
    }

    /**
     * Called when the Certificate store select changes
     */
    const onSelectCertificateStore = (certificateStore : CertificateStore) =>
    {
        // clear state
        // this is needed because otherwise certificates loaded from a different store might be validated
        // until the next render which results in back-end warnings about non-existing certificates
        setCertificates([]);
        setCertificateCount(0);
        setSelectedCertificates([]);

        setCertificateStore(certificateStore);
    }

    const trustSelectedCertificate = (status : CTLEntryStatus) =>
    {
        if (setSpinnerStateCounter !== undefined) {
            setSpinnerStateCounter(count => count + 1);
        }

        ctlApi.cTLControllerSetCTLs(status, false, selectedCertificates.map(c => c.thumbprint))
            .then(() =>
            {
                forceReload();
            })
            .catch((err: Error | AxiosError) => {
                logger.error("Error setting certificate trust", err);
            }).finally(() => {
            if (setSpinnerStateCounter !== undefined) {
                setSpinnerStateCounter(count => count - 1);
            }
            setSelectedCertificates([]);
        })
    }

    const resetTrustSelectedCertificate = () =>
    {
        if (setSpinnerStateCounter !== undefined) {
            setSpinnerStateCounter(count => count + 1);
        }

        ctlApi.cTLControllerDeleteCTLs(selectedCertificates.map(c => c.thumbprint))
            .then(() =>
            {
                forceReload();
            })
            .catch((err: Error | AxiosError) => {
                logger.error("Error resetting certificate trust", err);
            }).finally(() => {
            if (setSpinnerStateCounter !== undefined) {
                setSpinnerStateCounter(count => count - 1);
            }
            setSelectedCertificates([]);
        })
    }

    return (
        <>
            <ConfirmDialog title={t("certificates.confirm")}
                           bodyText={t("certificates.body-text")}
                           confirmButtonTitle={t("certificates.delete")}
                           onClickConfirmEvent={deleteSelected}
                           showConfirm={showDeleteConfirm}
                           setShowConfirm={setShowDeleteConfirm}
            />

            {showCertificateExportDialog && <CertificateExportDialog certificateStore={certificateStore}
                                                                     certificates={selectedCertificates}
                                                                     onClose={onExportCertificatesDialogClose} />}

            {showCertificateImportDialog && <CertificateImportDialog certificateStore={certificateStore}
                                                                     onClose={onCertificateImportDialogClose} />}

            {showPrivateKeyImportDialog && <PrivateKeyImportDialog certificateStore={certificateStore}
                                                                   onClose={onPrivateKeyImportDialogClose} />}

            <div className="d-flex flex-column h-100">
                <PageHeader icon={<SMIMEIcon />} title={t("certificates.title")} onClickReload={forceReload}/>

                <PageAlert message={errorMessage} setMessage={setErrorMessage}/>

                <div className="px-4">
                    <Row>
                        <div className="d-flex flex-row align-items-center mb-4">
                            <div className="d-flex justify-content-start">
                                <Form.Select
                                    aria-label={t("certificates.certificate-store")}
                                    value={certificateStore}
                                    onChange={(e) => onSelectCertificateStore(Utils.stringToEnum(CertificateStore, e.target.value as CertificateStore))}>
                                    <option value="CERTIFICATES">{t("certificates.certificates")}</option>
                                    <option value="ROOTS">{t("certificates.roots")}</option>
                                </Form.Select>
                            </div>
                            <div className="d-flex flex-fill justify-content-end align-items-center">
                                <div className="d-xl-flex gap-2">
                                    <CertificateFilter expired={expired}
                                                       setExpired={setExpired}
                                                       missingKeyAlias={missingKeyAlias}
                                                       setMissingKeyAlias={setMissingKeyAlias}
                                                       searchField={searchField}
                                                       setSearchField={setSearchField}
                                                       searchValue={searchValue}
                                                       setSearchValue={setSearchValue}
                                                       activeSearchValue={activeSearchValue}
                                                       setActiveSearchValue={setActiveSearchValue}
                                    />
                                </div>

                                <div className="ms-4"/>

                                <ActionsDropdown>
                                    <Dropdown.Item onClick={() => setShowCertificateImportDialog(true)}>
                                        <CertificateImportIcon className="me-2"/> {t("certificates.import-certificates")}
                                    </Dropdown.Item>
                                    <Dropdown.Item onClick={() => setShowPrivateKeyImportDialog(true)}>
                                        <KeyImportIcon className="me-2"/>  {t("certificates.import-private-keys")}
                                    </Dropdown.Item>
                                </ActionsDropdown>
                            </div>
                        </div>
                    </Row>
                </div>

                <PageContent className="flex-grow-1 min-h-0">
                    <Row className="h-100 w-100">
                        <Col className="h-100 overflow-auto">
                            <Table className="sticky sticky-x" bordered hover>
                                {/*Hide the default table header and show the selection menu if certificates are selected*/}
                                {selectedCertificates.length === 0 ?
                                    <CertificateTableHeader>
                                        {/* Add icon to first column which will invert selection when clicked */}
                                        <InvertSelection invertSelectionEvent={invertSelection}/>
                                    </CertificateTableHeader>
                                    :
                                    <thead className="sticky-top">
                                    <tr className="text-nowrap">
                                        <th colSpan={15}>
                                            <Navbar className="bg-body-tertiary ps-2">
                                                {/* Add icon to first column which will clear the selection when clicked */}
                                                <ClearSelection clearSelectionEvent={() => setSelectedCertificates([])} checked={true}/>

                                                <Navbar.Text className="ms-2">{selectedCertificates.length} selected</Navbar.Text>

                                                <div className="vr ms-4 me-2"></div>

                                                <Nav>
                                                    <Nav.Link onClick={() => setShowDeleteConfirm(true)}><DeleteIcon
                                                        className="text-danger"/> {t("certificates.delete-selected")}
                                                    </Nav.Link>
                                                    <Nav.Link
                                                        onClick={() => setShowCertificateExportDialog(true)}><ExportIcon
                                                        className="text-success"/> {t("certificates.export-selected")}
                                                    </Nav.Link>
                                                    <Nav.Link
                                                        onClick={() => trustSelectedCertificate(CTLEntryStatus.Whitelisted)}><TrustIcon
                                                        className="text-success"/> {t("certificates.trust-selected")}
                                                    </Nav.Link>
                                                    <Nav.Link
                                                        onClick={() => trustSelectedCertificate(CTLEntryStatus.Blacklisted)}><DistrustIcon
                                                        className="text-danger"/> {t("certificates.distrust-selected")}
                                                    </Nav.Link>
                                                    <Nav.Link
                                                        onClick={() => resetTrustSelectedCertificate()}><ResetTrustIcon
                                                        className="text-success"/> {t("certificates.reset-selected-trust")}
                                                    </Nav.Link>
                                                </Nav>
                                            </Navbar>
                                        </th>
                                    </tr>
                                    </thead>
                                }

                                <tbody>
                                {certificates.map((certificate) => (
                                    <CertificateRow key={certificate.thumbprint} certificate={certificate}
                                                    checkCertificateValidityCallback={checkCertificateValidity}
                                                    selected={selectedCertificates.find((scert) =>  scert.thumbprint === certificate.thumbprint) !== undefined}
                                    >
                                        {/* Add selection checkbox to first column */}
                                        <Form.Check
                                            type="checkbox"
                                            className="me-0"
                                            checked={selectedCertificates.find((scert) =>  scert.thumbprint === certificate.thumbprint) !== undefined}
                                            onChange={(e) => onSelectCertificate(e.target.checked, certificate)}
                                            inline
                                        />
                                    </CertificateRow>
                                ))}
                                </tbody>
                            </Table>
                        </Col>
                    </Row>
                </PageContent>

                <PageFooter
                    left={<RowsPerPage rowsPerPage={rowsPerPage} setRowsPerPage={setRowsPerPage} setCurrentPage={setCurrentPage}/>}
                    right={<Pager itemCount={certificateCount ?? 0}
                                  currentPage={currentPage}
                                  setCurrentPage={setCurrentPage}
                                  maxItemsPerPage={rowsPerPage}
                    />}
                />
            </div>
        </>
    );
}
